import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Delete as DeleteIcon,
  VideoLibrary,
  Image,
} from '@mui/icons-material';
import streaksService, {
  AdminStreak,
  StreakStatus,
} from '../../services/StreaksService';

const STATUS_COLORS: Record<StreakStatus, 'warning' | 'success' | 'error'> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
};

const StreaksPage: React.FC = () => {
  const [streaks, setStreaks] = useState<AdminStreak[]>([]);
  const [statusFilter, setStatusFilter] = useState<StreakStatus | ''>('PENDING');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdminStreak | null>(null);

  const [mediaViewerUrl, setMediaViewerUrl] = useState<string | null>(null);
  const [mediaViewerType, setMediaViewerType] = useState<'IMAGE' | 'VIDEO'>('IMAGE');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await streaksService.listStreaks(
        statusFilter ? { status: statusFilter } : undefined,
      );
      setStreaks(data);
    } catch {
      setError('Failed to load streaks.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (streakId: string) => {
    setActionLoading(streakId);
    setError(null);
    setNotice(null);
    try {
      const result = await streaksService.approveStreak(streakId);
      setStreaks((prev) => prev.filter((s) => s.id !== streakId));
      const msg =
        result.points.status === 'awarded'
          ? `Streak approved — ${result.points.pointsAwarded} points awarded.`
          : result.points.status === 'duplicate'
            ? 'Streak approved — points already issued (idempotent).'
            : 'Streak approved, but points award failed — check logs.';
      setNotice(msg);
    } catch {
      setError('Failed to approve streak.');
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectDialog = (streakId: string) => {
    setRejectTargetId(streakId);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectTargetId) return;
    setActionLoading(rejectTargetId);
    setRejectDialogOpen(false);
    setError(null);
    setNotice(null);
    try {
      await streaksService.rejectStreak(rejectTargetId, rejectReason || undefined);
      setStreaks((prev) => prev.filter((s) => s.id !== rejectTargetId));
      setNotice('Streak rejected.');
    } catch {
      setError('Failed to reject streak.');
    } finally {
      setActionLoading(null);
      setRejectTargetId(null);
    }
  };

  const openMedia = (streak: AdminStreak) => {
    setMediaViewerUrl(streak.mediaUrl);
    setMediaViewerType(streak.mediaType);
  };

  const openDeleteDialog = (streak: AdminStreak) => {
    setDeleteTarget(streak);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const targetId = deleteTarget.id;
    setActionLoading(targetId);
    setDeleteDialogOpen(false);
    setError(null);
    setNotice(null);
    try {
      await streaksService.deleteStreak(targetId);
      setStreaks((prev) => prev.filter((s) => s.id !== targetId));
      setNotice('Kheir story deleted.');
    } catch {
      setError('Failed to delete Kheir story.');
    } finally {
      setActionLoading(null);
      setDeleteTarget(null);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>
          Kheir Stories
        </Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e: SelectChangeEvent) =>
              setStatusFilter(e.target.value as StreakStatus | '')
            }
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="APPROVED">Approved</MenuItem>
            <MenuItem value="REJECTED">Rejected</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 2 }}>
          {notice}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Caption</TableCell>
                <TableCell>Media</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Points</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {streaks.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No streaks found</Typography>
                  </TableCell>
                </TableRow>
              )}
              {streaks.map((streak) => (
                <TableRow key={streak.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {streak.author.displayName ?? streak.userId.slice(0, 8) + '…'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {streak.userId.slice(0, 8)}…
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    <Typography variant="body2" noWrap title={streak.caption ?? ''}>
                      {streak.caption || <em style={{ opacity: 0.5 }}>—</em>}
                    </Typography>
                    {streak.rejectionReason && (
                      <Typography variant="caption" color="error">
                        Reason: {streak.rejectionReason}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        streak.mediaType === 'VIDEO' ? (
                          <VideoLibrary fontSize="small" />
                        ) : (
                          <Image fontSize="small" />
                        )
                      }
                      onClick={() => openMedia(streak)}
                      sx={{ textTransform: 'none', fontSize: 12 }}
                    >
                      View
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={streak.mediaType}
                      size="small"
                      variant="outlined"
                      color={streak.mediaType === 'VIDEO' ? 'secondary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={streak.status}
                      size="small"
                      color={STATUS_COLORS[streak.status]}
                    />
                  </TableCell>
                  <TableCell>
                    {streak.pointsAwarded ? (
                      <Chip label="Awarded" size="small" color="success" variant="outlined" />
                    ) : (
                      <Chip label="—" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(streak.submittedAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {streak.status === 'PENDING' && (
                      <Stack direction="row" spacing={0.5}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={
                            actionLoading === streak.id ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <CheckCircle fontSize="small" />
                            )
                          }
                          disabled={!!actionLoading}
                          onClick={() => handleApprove(streak.id)}
                          sx={{ textTransform: 'none' }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Cancel fontSize="small" />}
                          disabled={!!actionLoading}
                          onClick={() => openRejectDialog(streak.id)}
                          sx={{ textTransform: 'none' }}
                        >
                          Reject
                        </Button>
                      </Stack>
                    )}
                    {streak.status !== 'PENDING' && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {streak.reviewedAt
                            ? new Date(streak.reviewedAt).toLocaleDateString()
                            : 'Reviewed'}
                        </Typography>
                        {streak.status === 'APPROVED' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={
                              actionLoading === streak.id ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )
                            }
                            disabled={!!actionLoading}
                            onClick={() => openDeleteDialog(streak)}
                            sx={{ textTransform: 'none' }}
                          >
                            Delete
                          </Button>
                        )}
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Reject reason dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Kheir story</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Rejection reason (optional)"
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleRejectConfirm}>
            Confirm Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete approved Kheir story?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            This will remove the story from the public feed and the dashboard.
            The action is recorded in the audit log and cannot be undone from
            here.
          </Typography>
          {deleteTarget?.caption && (
            <Typography
              variant="body2"
              sx={{ mt: 1, fontStyle: 'italic' }}
              noWrap
              title={deleteTarget.caption}
            >
              “{deleteTarget.caption}”
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>
            Delete story
          </Button>
        </DialogActions>
      </Dialog>

      {/* Media viewer dialog */}
      <Dialog
        open={!!mediaViewerUrl}
        onClose={() => setMediaViewerUrl(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Media Preview</DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', pb: 3 }}>
          {mediaViewerType === 'VIDEO' ? (
            <video
              src={mediaViewerUrl ?? ''}
              controls
              style={{ maxWidth: '100%', maxHeight: 480 }}
            />
          ) : (
            <img
              src={mediaViewerUrl ?? ''}
              alt="Streak media"
              style={{ maxWidth: '100%', maxHeight: 480, objectFit: 'contain' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMediaViewerUrl(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StreaksPage;
